import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import 'react-dropzone-uploader/dist/styles.css';
// utils
import { compressImage } from '../../../utils/imageCompression';
// components
import { FormProvider, RHFCheckbox, RHFTextField } from '../../../components/hook-form';
import tracker from '../../../api/tracker';
import RHFFileInput from '../../../components/hook-form/RHFFileInput';
import Label from '../../../components/Label';

// ----------------------------------------------------------------------

export default function PostForm({ post }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [body, setBody] = useState(post ? post.body : '');
  const [errorMessage, setErrorMessage] = useState(null);
  const isEdit = !!post;

  useEffect(() => {
    if (post) {
      const fields = ['title', 'summary', 'body', 'source', 'active'];
      fields.forEach((field) => setValue(field, post[field]));
      setValue('image', post.image ? post.image : null);
      setValue('thumbnail', post.thumbnail ? post.thumbnail : null);
    }
  }, [post]);

  const PostSchema = Yup.object().shape({
    title: Yup.string().required(t('Required')),
    summary: Yup.string().required(t('Required')),
    source: Yup.string().default(' '),
    image: isEdit
      ? Yup.mixed().nullable()
      : Yup.mixed()
          .required(t('Required'))
          .test('fileType', 'Solo se permiten imágenes (JPG, JPEG, PNG)', (value) => {
            console.log('Validating image:', value);
            if (!value || typeof value === 'string') return true;
            if (value instanceof FileList) {
              if (value.length === 0) return false;
              const file = value[0];
              const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
              return allowedTypes.includes(file.type);
            }
            if (value instanceof File) {
              const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
              return allowedTypes.includes(value.type);
            }
            return false;
          }),
    thumbnail: isEdit
      ? Yup.mixed().nullable()
      : Yup.mixed()
          .required(t('Required'))
          .test('fileType', 'Solo se permiten imágenes (JPG, JPEG, PNG)', (value) => {
            console.log('Validating thumbnail:', value);
            if (!value || typeof value === 'string') return true;
            if (value instanceof FileList) {
              if (value.length === 0) return false;
              const file = value[0];
              const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
              return allowedTypes.includes(file.type);
            }
            if (value instanceof File) {
              const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
              return allowedTypes.includes(value.type);
            }
            return false;
          }),
  });

  const defaultValues = {
    title: '',
    summary: '',
    body: '',
    image: null,
    thumbnail: null,
    active: post ? post.active : true,
  };

  const methods = useForm({
    resolver: yupResolver(PostSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (data) => {
    setErrorMessage(null);

    if (data.image instanceof FileList) {
      data.image = data.image[0];
    }
    if (data.image && data.image instanceof File) {
      try {
        if (data.image.type.startsWith('image/')) {
          console.log('Compressing image:', data.image);
          const compressedImage = await compressImage(data.image, {
            maxSizeMB: 5,
            maxWidthOrHeight: 1200,
          });
          console.log('Compressed image:', compressedImage);
          data.image = compressedImage;
        } else {
          throw new Error(t('La imagen principal debe ser un archivo de imagen (JPG, JPEG, PNG).'));
        }
      } catch (error) {
        console.error('Error compressing image:', error);
        setErrorMessage(error.message || t('Error al comprimir la imagen principal.'));
        return;
      }
    } else if (isEdit && !data.image) {
      data.image = post.image;
    }
    if (data.thumbnail instanceof FileList) {
      data.thumbnail = data.thumbnail[0];
    }
    if (data.thumbnail && data.thumbnail instanceof File) {
      try {
        if (data.thumbnail.type.startsWith('image/')) {
          console.log('Compressing thumbnail:', data.thumbnail);
          const compressedThumbnail = await compressImage(data.thumbnail, {
            maxSizeMB: 5,
            maxWidthOrHeight: 800,
          });
          console.log('Compressed thumbnail:', compressedThumbnail);
          data.thumbnail = compressedThumbnail;
        } else {
          throw new Error(t('El thumbnail debe ser un archivo de imagen (JPG, JPEG, PNG).'));
        }
      } catch (error) {
        console.error('Error compressing thumbnail:', error);
        setErrorMessage(error.message || t('Error al comprimir el thumbnail.'));
        return;
      }
    } else if (isEdit && !data.thumbnail) {
      data.thumbnail = post.thumbnail;
    }

    data.body = body;

    const formData = new FormData();
    formData.append('title', data.title);
    formData.append('summary', data.summary);
    formData.append('body', data.body);
    formData.append('source', data.source || '');
    formData.append('active', data.active);
    if (data.image instanceof File) {
      formData.append('image', data.image);
    } else if (isEdit && data.image) {
      formData.append('image', data.image);
    }
    if (data.thumbnail instanceof File) {
      formData.append('thumbnail', data.thumbnail);
    } else if (isEdit && data.thumbnail) {
      formData.append('thumbnail', data.thumbnail);
    }

    console.log('FormData to submit:', formData);

    try {
      if (!post) {
        await tracker.post('articles', formData);
      } else {
        await tracker.post(`articles/update/${post.hashid}`, formData);
      }
      navigate('/dashboard/blog', { replace: true });
    } catch (error) {
      console.error('Error submitting post:', error.response?.data || error.message);
      setErrorMessage(error.response?.data?.message || t('Error al guardar la publicación.'));
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {errorMessage && (
          <Typography color="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Typography>
        )}
        <Stack direction="column" spacing={2}>
          <RHFTextField name="title" label={t('Title')} />
          <RHFTextField name="summary" label={t('Summary')} />
          <ReactQuill style={{ height: '300px', marginBottom: '50px' }} theme="snow" value={body} onChange={setBody} />
          <RHFTextField name="source" label={t('source')} />
          <Label>{t('Imagen')}</Label>
          <RHFFileInput name="image" fileUrl={post ? post.image : null} />
          <Label>{t('Thumbnail')}</Label>
          <RHFFileInput name="thumbnail" fileUrl={post ? post.thumbnail : null} />
          <RHFCheckbox name="active" label={t('Activo')} />{' '}
        </Stack>

        <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
          {t('Save')}
        </LoadingButton>
      </Stack>
    </FormProvider>
  );
}
